/**
 * Fulfillments Redux Slice
 * 
 * Clean state management for fulfillments list with filtering and pagination.
 * Follows single responsibility - only manages fulfillment list state.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import type {
  FulfillmentResponse,
  FulfillmentQueryParams,
  FulfillmentStatusCounts,
} from '@/app/types/fulfillment';
import { fulfillmentService } from '@/app/services/fulfillmentService';

// ============================================================================
// Types
// ============================================================================

interface FulfillmentListResponse {
  fulfillments: FulfillmentResponse[];
  total: number;
}

interface FulfillmentsState {
  items: FulfillmentResponse[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  filters: FulfillmentQueryParams;
  statusCounts: FulfillmentStatusCounts | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

// ============================================================================
// Constants
// ============================================================================

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export type PageSizeOption = typeof PAGE_SIZE_OPTIONS[number];

const DEFAULT_PAGE_SIZE: PageSizeOption = 10;

// ============================================================================
// Initial State
// ============================================================================

const initialFilters: FulfillmentQueryParams = {
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

const initialState: FulfillmentsState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  filters: initialFilters,
  statusCounts: null,
  status: 'idle',
  error: null,
};

// ============================================================================
// Async Thunks
// ============================================================================

export const fetchFulfillments = createAsyncThunk<
  FulfillmentListResponse,
  FulfillmentQueryParams | undefined,
  { state: RootState; rejectValue: string }
>(
  'fulfillments/fetch',
  async (params, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const filters = params ?? state.fulfillments.filters;
      return await fulfillmentService.queryFulfillments(filters);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch fulfillments'
      );
    }
  }
);

export const fetchStatusCounts = createAsyncThunk<
  FulfillmentStatusCounts,
  void,
  { rejectValue: string }
>(
  'fulfillments/fetchStatusCounts',
  async (_, { rejectWithValue }) => {
    try {
      return await fulfillmentService.getStatusCounts();
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch status counts'
      );
    }
  }
);

// ============================================================================
// Slice
// ============================================================================

const fulfillmentsSlice = createSlice({
  name: 'fulfillments',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<FulfillmentQueryParams>>) => {
      // Merge new filters with existing, reset to page 1
      state.filters = {
        ...state.filters,
        ...action.payload,
        offset: 0, // Reset pagination when filters change
      };
      state.currentPage = 1;
    },
    
    setPage: (state, action: PayloadAction<number>) => {
      const page = action.payload;
      state.currentPage = page;
      state.filters.offset = (page - 1) * state.pageSize;
    },
    
    setPageSize: (state, action: PayloadAction<PageSizeOption>) => {
      const newSize = action.payload;
      state.pageSize = newSize;
      state.filters.limit = newSize;
      state.filters.offset = 0;
      state.currentPage = 1;
    },
    
    clearFilters: (state) => {
      state.filters = { ...initialFilters, limit: state.pageSize };
      state.currentPage = 1;
    },
    
    updateFulfillmentInList: (state, action: PayloadAction<FulfillmentResponse>) => {
      const index = state.items.findIndex(
        f => f.fulfillment_id === action.payload.fulfillment_id
      );
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch fulfillments
      .addCase(fetchFulfillments.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchFulfillments.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.fulfillments;
        state.totalCount = action.payload.total;
      })
      .addCase(fetchFulfillments.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Unknown error';
      })
      // Fetch status counts
      .addCase(fetchStatusCounts.fulfilled, (state, action) => {
        state.statusCounts = action.payload;
      });
  },
});

// ============================================================================
// Actions & Selectors
// ============================================================================

export const { 
  setFilters, 
  setPage, 
  setPageSize,
  clearFilters, 
  updateFulfillmentInList,
  resetState 
} = fulfillmentsSlice.actions;

// Selectors
export const selectFulfillments = (state: RootState) => state.fulfillments.items;
export const selectFulfillmentsTotalCount = (state: RootState) => state.fulfillments.totalCount;
export const selectFulfillmentsCurrentPage = (state: RootState) => state.fulfillments.currentPage;
export const selectFulfillmentsPageSize = (state: RootState) => state.fulfillments.pageSize;
export const selectFulfillmentsFilters = (state: RootState) => state.fulfillments.filters;
export const selectFulfillmentsStatus = (state: RootState) => state.fulfillments.status;
export const selectFulfillmentsError = (state: RootState) => state.fulfillments.error;
export const selectFulfillmentsStatusCounts = (state: RootState) => state.fulfillments.statusCounts;

// Derived selectors
export const selectFulfillmentsIsLoading = (state: RootState) => 
  state.fulfillments.status === 'loading';

export const selectFulfillmentsTotalPages = (state: RootState) => 
  Math.ceil(state.fulfillments.totalCount / state.fulfillments.pageSize);

export default fulfillmentsSlice.reducer;

